import {
  BIBLIOTECA_LIST_ENDPOINT,
  getBibliotecaDetailUrl,
  LOGIN_REDIRECT_URL,
} from '../../externalUrls';
import type {
  BibliotecaDocumentDetail,
  BibliotecaListFilters,
  BibliotecaListResponse,
} from './types';

export class BibliotecaApiError extends Error {
  constructor(readonly status: number) {
    super(`Biblioteca request failed with HTTP ${status}.`);
    this.name = 'BibliotecaApiError';
  }
}

function redirectIfSessionExpired(status: number): void {
  if (status === 401 || status === 403) {
    window.location.assign(LOGIN_REDIRECT_URL);
  }
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    redirectIfSessionExpired(response.status);
    throw new BibliotecaApiError(response.status);
  }

  return (await response.json()) as T;
}

function appendTextParameter(
  parameters: URLSearchParams,
  name: string,
  value: string | undefined,
): void {
  const normalized = value?.trim();
  if (normalized) {
    parameters.set(name, normalized);
  }
}

export function getBibliotecaListUrl(filters: BibliotecaListFilters = {}): string {
  const parameters = new URLSearchParams();

  appendTextParameter(parameters, 'q', filters.q);
  appendTextParameter(parameters, 'fuente', filters.fuente);
  appendTextParameter(parameters, 'tipo', filters.tipo);
  appendTextParameter(parameters, 'numero', filters.numero);
  appendTextParameter(parameters, 'entidad', filters.entidad);

  if (typeof filters.anio === 'number' && Number.isInteger(filters.anio)) {
    parameters.set('anio', String(filters.anio));
  }

  if (
    typeof filters.pagina === 'number'
    && Number.isInteger(filters.pagina)
    && filters.pagina > 0
  ) {
    parameters.set('pagina', String(filters.pagina));
  }

  const query = parameters.toString();
  return query ? `${BIBLIOTECA_LIST_ENDPOINT}?${query}` : BIBLIOTECA_LIST_ENDPOINT;
}

export function listBibliotecaDocuments(
  filters: BibliotecaListFilters = {},
): Promise<BibliotecaListResponse> {
  return getJson<BibliotecaListResponse>(getBibliotecaListUrl(filters));
}

export function getBibliotecaDocument(
  documentId: number,
): Promise<BibliotecaDocumentDetail> {
  return getJson<BibliotecaDocumentDetail>(getBibliotecaDetailUrl(documentId));
}
